import { StringContext } from "../../core/StringContext"
import { StringData } from "../../core/StringData"
import { StringModule } from "../../core/StringModule"

/**
 * Visual tracker that plots scroll displacement (velocity) in real time.
 * Useful for debugging and tuning smoothing behavior.
 */
export class StringDelayLerpTracker extends StringModule {
  private canvas!: HTMLCanvasElement
  private context!: CanvasRenderingContext2D
  private history: number[] = []

  private maxPoints = 0
  private height = 0
  private value = 0
  private target = 0

  constructor(context: StringContext) {
    super(context)
    this._type = 2
  }

  /**
   * Called when the module starts — sets up canvas.
   */
  onInit(): void {
    this.initCanvas()
    this.maxPoints = this.canvas.width
  }

  /**
   * Called on scroll — stores current displacement and redraws.
   */
  onScroll(data: StringData): void {
    const d = Math.abs(data.scroll.displacement)
    this.value = d
    this.history.push(d)

    if (this.history.length > this.maxPoints) {
      this.history.shift()
    }

    this.draw()
  }

  /**
   * Draws the displacement graph to canvas.
   */
  private draw(): void {
    const ctx = this.context
    const w = this.canvas.width
    const h = this.canvas.height

    ctx.clearRect(0, 0, w, h)

    ctx.strokeStyle = "red"
    ctx.lineWidth = 2
    ctx.beginPath()

    this.history.forEach((val, i) => {
      const x = i
      const y = h - val * this.height
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    })

    ctx.stroke()
  }

  /**
   * Creates and styles the tracking canvas.
   */
  private initCanvas(): void {
    const canvas = document.createElement("canvas")
    const width = window.innerWidth * 0.5
    this.height = window.innerHeight / 15 - 20

    canvas.width = width
    canvas.height = this.height

    Object.assign(canvas.style, {
      position: "fixed",
      bottom: `${window.innerHeight / 20 + 10}px`,
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: "#000000",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      zIndex: "1000",
      pointerEvents: "none",
    })

    this.canvas = canvas
    this.context = canvas.getContext("2d")!
    document.body.appendChild(canvas)
  }

  /**
   * Optional method to update external comparison target.
   */
  public setTarget(position: number): void {
    this.target = position
  }

  /**
   * Removes the canvas from DOM and resets.
   */
  public clear(): void {
    this.canvas.remove()
    this.history = []
  }
}
